/* eslint-disable camelcase */
import { Component, Host, Prop, h, State, Event, EventEmitter, Fragment, Watch, Build } from '@stencil/core';
import slugify from 'slugify';
import { AppService, DomService, RCM_USER_TYPE_INSTI, RCM_USER_TYPE_RETAIL, StoryblokService, StringService } from '../../services';
import { FundApiService } from '../../services/fund-api';
import { TrackingService } from '../../services/tracking';
import { TrackingObjectBuilder } from '../../services/tracking/tracking-object-builder';
import { Target } from '../types/link.types';

@Component({
  tag: 'rcm-web-search-results',
  styleUrl: 'rcm-web-search-results.scss',
  shadow: true,
})
export class RcmWebSearchResults {
  @Prop() value: string;

  @Prop() show = false;

  @State() funds = null;

  @State() fundsItems = null;

  @State() pagesAndArticles = null;

  @State() pagesAndArticlesItems = null;

  @State() allFoundFunds = null;

  @State() searchValue: string;

  currentUserType: string;

  @Event({ eventName: 'close-web-search' }) closeWebSearch: EventEmitter;

  async componentWillLoad() {
    if (Build.isBrowser) {
      this.currentUserType = AppService.getUserType();
      const channel = AppService.getChannelByCurrentUserType();

      FundApiService.getFundsList(channel).then((fundslist) => {
        this.funds = fundslist.fondsListe;
        if (this.value) {
          this.handleSearch();
        }
      });
    }
  }

  @Watch('value')
  async handleSearch() {
    const searchValue = this.value.toLowerCase();
    const { slug } = DomService.getGlobalContent();

    TrackingService.push(
      new TrackingObjectBuilder()
        .forFieldSubmit({
          formName: 'rcm-web-search',
          fieldName: 'search',
          fieldValue: this.value,
        })
        .build(),
    );

    if (Array.isArray(this.funds)) {
      this.allFoundFunds = this.funds.filter(
        (fund) =>
          fund.fondsName.replaceAll('-', ' ').toLowerCase().includes(searchValue) ||
          fund.tranchen.some((tranche) => tranche.isin.toLowerCase().includes(searchValue)),
      );
      this.fundsItems = this.allFoundFunds.slice(0, 5);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const articles = await StoryblokService.getStoryblokStories(null, searchValue);
    const userTypeSlug = this.currentUserType === RCM_USER_TYPE_INSTI ? slug.institutional : slug.retail;
    const stories = [
      ...articles.filter(
        (page) =>
          // eslint-disable-next-line camelcase
          this.currentUserType === '' || page.content?.user_type?.includes(this.currentUserType) || page?.full_slug?.includes(userTypeSlug),
      ),
    ];
    this.pagesAndArticles = stories.map((story) => ({
      headline: story.content?.headline || story.content?.navigation_title,
      text: story.content?.text || story.content?.navigation_subline,
      link: story.full_slug,
    }));
    this.pagesAndArticlesItems = this.pagesAndArticles.slice(0, 5);

    if (!this.pagesAndArticlesItems.length && !this.fundsItems?.length) {
      TrackingService.push(
        new TrackingObjectBuilder()
          .forFormError({
            formName: 'rcm-web-search',
            errorMessage: `${DomService.getGlobalContent().webSearch?.noResults} ${this.value}`,
          })
          .build(),
      );
    } else {
      TrackingService.push(
        new TrackingObjectBuilder()
          .forFormSuccess({
            formName: 'rcm-web-search',
          })
          .build(),
      );
    }
  }

  render() {
    const content = DomService.getGlobalContent().webSearch;

    const errorMessage = (
      <rcm-column class="error-message">
        <div>
          {content?.noResults} &quot;{this.value}&quot;
        </div>
      </rcm-column>
    );

    const getErrorMessage = () => (
      <rcm-column class="error-message">
        <div>
          {content?.noResults} &quot;{this.value}&quot;
        </div>
      </rcm-column>
    );

    const fundsResultsNumber = this.funds ? `(${this.allFoundFunds.length})` : '';

    const pagesAndArticleResultsNumber = this.pagesAndArticles ? `(${this.pagesAndArticles.length})` : '';

    const callToAction =
      this.currentUserType === RCM_USER_TYPE_INSTI || !content?.callToActionButtonText ? null : (
        <div class="action-call">
          <rcm-headline level={3} headline={content?.callToActionText}></rcm-headline>
          <rcm-button link={{ src: content?.fundFinderLink, target: Target.BLANK }} text={content?.callToActionButtonText}></rcm-button>
        </div>
      );

    const showMoreDefaultText = 'Show more';

    return (
      <Host>
        <rcm-modal show={this.show} without-padding fullscreen with-search>
          <rcm-container class="results__container">
            <div>
              <div class="legend">
                <rcm-headline level={3} headline={`${content?.fundsHeader} ${fundsResultsNumber}`} class="legend-text"></rcm-headline>
                <a href={StringService.normalizeLink(content?.allFundsLink[this.currentUserType])} class="legend-link">
                  {content?.allFundsLabel}
                </a>
              </div>
              <rcm-columns multipleRows class="list funds-list" key={Math.random()}>
                {!this.funds ? (
                  <rcm-column span={12}>
                    <rcm-loader />
                  </rcm-column>
                ) : (
                  <Fragment>
                    {this.fundsItems.map(({ fondsName, isin, veranlagungszieltext, tranchen, primaertranchen }) => (
                      <rcm-column
                        span={12}
                        key={fondsName}
                        class="list__item"
                        onClick={() => {
                          const { slug } = DomService.getGlobalContent();
                          const channel =
                            window.location.pathname.includes(slug.retail) || this.currentUserType === RCM_USER_TYPE_RETAIL
                              ? RCM_USER_TYPE_RETAIL
                              : RCM_USER_TYPE_INSTI;
                          const goTo = `${StringService.normalizeLink(content?.allFundsLink[channel])}/detail/${slugify(fondsName)}`;

                          TrackingService.push(
                            new TrackingObjectBuilder()
                              .forLinkClick({
                                clickUrl: goTo,
                                clickText: fondsName,
                                parentComponent: 'rcm-web-search-results',
                              })
                              .build(),
                          );

                          window.location.assign(goTo);
                        }}
                      >
                        <rcm-headline headline={fondsName} level={3} />
                        <p class="description">
                          {isin || tranchen.filter((t) => t.hash === primaertranchen?.[0].hash)[0]?.isin || tranchen[0]?.isin}
                        </p>
                        <p class="description">{veranlagungszieltext}</p>
                      </rcm-column>
                    ))}
                    {!this.fundsItems.length && getErrorMessage()}
                    {this.fundsItems.length < 5 && callToAction}
                    {this.allFoundFunds.length > 5 && this.allFoundFunds.length !== this.fundsItems.length && (
                      <rcm-button
                        class="show-more-button"
                        text={content?.showMoreText || showMoreDefaultText}
                        onRcmButtonClick={() => {
                          this.fundsItems = this.allFoundFunds;
                        }}
                      ></rcm-button>
                    )}
                  </Fragment>
                )}
              </rcm-columns>
            </div>
            <div>
              <div class="legend">
                <rcm-headline
                  level={3}
                  headline={`${content?.pageAndArticlesHeader} ${pagesAndArticleResultsNumber}`}
                  class="legend-text"
                ></rcm-headline>
              </div>
              {!this.pagesAndArticles ? (
                <rcm-column span={12}>
                  <rcm-loader />
                </rcm-column>
              ) : (
                <rcm-columns multipleRows class="list">
                  {this.pagesAndArticlesItems.map(({ headline, text, link }) => (
                    <rcm-column
                      span={12}
                      key={headline + Date.now()}
                      class="list__item"
                      onClick={() => {
                        TrackingService.push(
                          new TrackingObjectBuilder()
                            .forLinkClick({
                              clickUrl: link,
                              clickText: headline,
                              parentComponent: 'rcm-web-search-results',
                            })
                            .build(),
                        );
                        DomService.visitSite(StringService.normalizeLink(link));
                      }}
                    >
                      <rcm-headline headline={headline} level={3} />
                      <p class="description">{text?.length > 240 ? `${text?.substring(0, 240)}...` : text}</p>
                    </rcm-column>
                  ))}
                  {this.pagesAndArticles.length > 5 && this.pagesAndArticles.length !== this.pagesAndArticlesItems.length && (
                    <rcm-button
                      class="show-more-button"
                      text={content?.showMoreText || 'Show more'}
                      onRcmButtonClick={() => {
                        this.pagesAndArticlesItems = this.pagesAndArticles;
                      }}
                    ></rcm-button>
                  )}
                  {!this.pagesAndArticlesItems.length && errorMessage}
                </rcm-columns>
              )}
            </div>
          </rcm-container>
        </rcm-modal>
      </Host>
    );
  }
}
